import React, { useState, useCallback } from 'react';

// Types for different proof visualizations
export type ProofLayout = 'overlapping-squares' | 'rotated-square' | 'four-rectangles' | 'simple-square';

export interface AlgebraicProofProps {
  layout: ProofLayout;
  targetArea: string; // e.g., "(a+b)²", "2ab", "a²", "b²", "c²"
  showLabels?: boolean;
  interactive?: boolean;
  selectedShapes?: string[];
  correctShapes?: string[];
  onSelectionChange?: (shapes: string[]) => void;
  isSubmitted?: boolean;
  isCorrect?: boolean;
  size?: number;
}

const AlgebraicProof: React.FC<AlgebraicProofProps> = ({
  layout,
  showLabels = true,
  interactive = false,
  selectedShapes = [],
  correctShapes = [],
  onSelectionChange,
  isSubmitted = false,
  size = 300,
}) => {
  const [hoveredShape, setHoveredShape] = useState<string | null>(null);

  const handleShapeClick = useCallback((shapeId: string) => {
    if (!interactive || isSubmitted) return;

    const newSelection = selectedShapes.includes(shapeId)
      ? selectedShapes.filter(s => s !== shapeId)
      : [...selectedShapes, shapeId];

    onSelectionChange?.(newSelection);
  }, [interactive, isSubmitted, selectedShapes, onSelectionChange]);

  const getShapeColor = (shapeId: string, baseColor: string) => {
    if (isSubmitted) {
      const isSelected = selectedShapes.includes(shapeId);
      const isCorrectShape = correctShapes.includes(shapeId);
      if (isSelected && isCorrectShape) return '#10b981'; // green
      if (isSelected && !isCorrectShape) return '#ef4444'; // red
      if (!isSelected && isCorrectShape) return '#f59e0b'; // orange (missed)
      return baseColor;
    }

    if (selectedShapes.includes(shapeId)) return '#818cf8'; // selected purple
    if (hoveredShape === shapeId && interactive) return '#a5b4fc'; // hover
    return baseColor;
  };

  const renderOverlappingSquares = () => {
    // Two overlapping squares showing (a+b)² visualization
    const a = size * 0.45;
    const b = size * 0.35;
    const gap = size * 0.1;

    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Bottom-left square (a×a) - light blue */}
        <rect
          x={gap}
          y={size - a - gap}
          width={a}
          height={a}
          fill={getShapeColor('a2-bottom', '#bfdbfe')}
          stroke="#1e293b"
          strokeWidth="2"
          style={{ cursor: interactive ? 'pointer' : 'default' }}
          onClick={() => handleShapeClick('a2-bottom')}
          onMouseEnter={() => setHoveredShape('a2-bottom')}
          onMouseLeave={() => setHoveredShape(null)}
        />

        {/* Top-right square (a×a) - darker blue */}
        <rect
          x={gap + a - b}
          y={gap}
          width={a}
          height={a}
          fill={getShapeColor('a2-top', '#93c5fd')}
          stroke="#1e293b"
          strokeWidth="2"
          style={{ cursor: interactive ? 'pointer' : 'default' }}
          onClick={() => handleShapeClick('a2-top')}
          onMouseEnter={() => setHoveredShape('a2-top')}
          onMouseLeave={() => setHoveredShape(null)}
        />

        {/* Overlap region (b×b) - white */}
        <rect
          x={gap + a - b}
          y={size - a - gap}
          width={b}
          height={b}
          fill={getShapeColor('b2-overlap', '#ffffff')}
          stroke="#1e293b"
          strokeWidth="2"
          style={{ cursor: interactive ? 'pointer' : 'default' }}
          onClick={() => handleShapeClick('b2-overlap')}
          onMouseEnter={() => setHoveredShape('b2-overlap')}
          onMouseLeave={() => setHoveredShape(null)}
        />

        {/* Labels */}
        {showLabels && (
          <>
            <text x={gap + a/3} y={size - gap - a/2} fontSize="16" fontStyle="italic" fill="#1e293b">a</text>
            <text x={gap + a - b/2} y={gap + a/3} fontSize="16" fontStyle="italic" fill="#1e293b">b</text>
            <text x={gap + a - b/2} y={size - gap - a + b/2} fontSize="16" fontStyle="italic" fill="#1e293b">b</text>
            <text x={gap + a + 10} y={size - gap - a/2} fontSize="16" fontStyle="italic" fill="#1e293b">a</text>
            <text x={gap + a/2} y={size - gap + 20} fontSize="16" fontStyle="italic" fill="#1e293b">a</text>
            <text x={gap + a - b/2} y={size - gap + 20} fontSize="16" fontStyle="italic" fill="#1e293b">b</text>
          </>
        )}
      </svg>
    );
  };

  const renderRotatedSquare = () => {
    // Square with rotated square inside (Pythagorean style proof)
    const outer = size - 40;
    const offset = 20;
    const aRatio = 0.6;
    const a = outer * aRatio;
    const b = outer * (1 - aRatio);

    // Corner points of inner rotated square
    const p1 = { x: offset + a, y: offset };
    const p2 = { x: offset + outer, y: offset + a };
    const p3 = { x: offset + b, y: offset + outer };
    const p4 = { x: offset, y: offset + b };

    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Outer square border */}
        <rect
          x={offset}
          y={offset}
          width={outer}
          height={outer}
          fill="none"
          stroke="#1e293b"
          strokeWidth="2"
        />

        {/* Top-right triangle */}
        <polygon
          points={`${p1.x},${p1.y} ${offset + outer},${offset} ${p2.x},${p2.y}`}
          fill={getShapeColor('tri-tr', '#93c5fd')}
          stroke="#1e293b"
          strokeWidth="2"
          style={{ cursor: interactive ? 'pointer' : 'default' }}
          onClick={() => handleShapeClick('tri-tr')}
          onMouseEnter={() => setHoveredShape('tri-tr')}
          onMouseLeave={() => setHoveredShape(null)}
        />

        {/* Bottom-right triangle */}
        <polygon
          points={`${p2.x},${p2.y} ${offset + outer},${offset + outer} ${p3.x},${p3.y}`}
          fill={getShapeColor('tri-br', '#93c5fd')}
          stroke="#1e293b"
          strokeWidth="2"
          style={{ cursor: interactive ? 'pointer' : 'default' }}
          onClick={() => handleShapeClick('tri-br')}
          onMouseEnter={() => setHoveredShape('tri-br')}
          onMouseLeave={() => setHoveredShape(null)}
        />

        {/* Bottom-left triangle */}
        <polygon
          points={`${p3.x},${p3.y} ${offset},${offset + outer} ${p4.x},${p4.y}`}
          fill={getShapeColor('tri-bl', '#93c5fd')}
          stroke="#1e293b"
          strokeWidth="2"
          style={{ cursor: interactive ? 'pointer' : 'default' }}
          onClick={() => handleShapeClick('tri-bl')}
          onMouseEnter={() => setHoveredShape('tri-bl')}
          onMouseLeave={() => setHoveredShape(null)}
        />

        {/* Top-left triangle */}
        <polygon
          points={`${p4.x},${p4.y} ${offset},${offset} ${p1.x},${p1.y}`}
          fill={getShapeColor('tri-tl', '#93c5fd')}
          stroke="#1e293b"
          strokeWidth="2"
          style={{ cursor: interactive ? 'pointer' : 'default' }}
          onClick={() => handleShapeClick('tri-tl')}
          onMouseEnter={() => setHoveredShape('tri-tl')}
          onMouseLeave={() => setHoveredShape(null)}
        />

        {/* Inner rotated square (c²) */}
        <polygon
          points={`${p1.x},${p1.y} ${p2.x},${p2.y} ${p3.x},${p3.y} ${p4.x},${p4.y}`}
          fill={getShapeColor('c2', '#ffffff')}
          stroke="#1e293b"
          strokeWidth="2"
          style={{ cursor: interactive ? 'pointer' : 'default' }}
          onClick={() => handleShapeClick('c2')}
          onMouseEnter={() => setHoveredShape('c2')}
          onMouseLeave={() => setHoveredShape(null)}
        />

        {/* Labels */}
        {showLabels && (
          <>
            <text x={offset + a/2} y={offset - 5} fontSize="14" fontStyle="italic" fill="#1e293b" textAnchor="middle">a</text>
            <text x={offset + a + b/2} y={offset - 5} fontSize="14" fontStyle="italic" fill="#1e293b" textAnchor="middle">b</text>
            <text x={offset + outer + 15} y={offset + a/2} fontSize="14" fontStyle="italic" fill="#1e293b">a</text>
            <text x={offset + outer + 15} y={offset + a + b/2} fontSize="14" fontStyle="italic" fill="#1e293b">b</text>
            <text x={(p1.x + p3.x)/2 + 10} y={(p1.y + p3.y)/2} fontSize="14" fontStyle="italic" fill="#1e293b">c</text>
          </>
        )}
      </svg>
    );
  };

  const renderFourRectangles = () => {
    // Square divided into a², b², and two ab rectangles
    const outer = size - 80; // More padding for labels
    const offset = 40; // Increased offset for left/top labels
    const aRatio = 0.6;
    const a = outer * aRatio;
    const b = outer * (1 - aRatio);

    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="overflow-visible">
        {/* Top-left: a² */}
        <rect
          x={offset}
          y={offset}
          width={a}
          height={a}
          fill={getShapeColor('a2', '#ffffff')}
          stroke="#1e293b"
          strokeWidth="2"
          style={{ cursor: interactive ? 'pointer' : 'default' }}
          onClick={() => handleShapeClick('a2')}
          onMouseEnter={() => setHoveredShape('a2')}
          onMouseLeave={() => setHoveredShape(null)}
        />

        {/* Top-right: ab */}
        <rect
          x={offset + a}
          y={offset}
          width={b}
          height={a}
          fill={getShapeColor('ab-top', '#93c5fd')}
          stroke="#1e293b"
          strokeWidth="2"
          style={{ cursor: interactive ? 'pointer' : 'default' }}
          onClick={() => handleShapeClick('ab-top')}
          onMouseEnter={() => setHoveredShape('ab-top')}
          onMouseLeave={() => setHoveredShape(null)}
        />

        {/* Bottom-left: ab */}
        <rect
          x={offset}
          y={offset + a}
          width={a}
          height={b}
          fill={getShapeColor('ab-bottom', '#93c5fd')}
          stroke="#1e293b"
          strokeWidth="2"
          style={{ cursor: interactive ? 'pointer' : 'default' }}
          onClick={() => handleShapeClick('ab-bottom')}
          onMouseEnter={() => setHoveredShape('ab-bottom')}
          onMouseLeave={() => setHoveredShape(null)}
        />

        {/* Bottom-right: b² */}
        <rect
          x={offset + a}
          y={offset + a}
          width={b}
          height={b}
          fill={getShapeColor('b2', '#ffffff')}
          stroke="#1e293b"
          strokeWidth="2"
          style={{ cursor: interactive ? 'pointer' : 'default' }}
          onClick={() => handleShapeClick('b2')}
          onMouseEnter={() => setHoveredShape('b2')}
          onMouseLeave={() => setHoveredShape(null)}
        />

        {/* Diagonal line in bottom-left - removed as per original design */}

        {/* Labels - positioned outside the squares with light color for dark bg */}
        {showLabels && (
          <>
            {/* Left side labels */}
            <text x={offset - 20} y={offset + a/2} fontSize="16" fontWeight="bold" fontStyle="italic" fill="#e2e8f0" textAnchor="middle" dominantBaseline="middle">a</text>
            <text x={offset - 20} y={offset + a + b/2} fontSize="16" fontWeight="bold" fontStyle="italic" fill="#e2e8f0" textAnchor="middle" dominantBaseline="middle">b</text>

            {/* Bottom labels */}
            <text x={offset + a/2} y={offset + outer + 25} fontSize="16" fontWeight="bold" fontStyle="italic" fill="#e2e8f0" textAnchor="middle">a</text>
            <text x={offset + a + b/2} y={offset + outer + 25} fontSize="16" fontWeight="bold" fontStyle="italic" fill="#e2e8f0" textAnchor="middle">b</text>

            {/* Shape labels inside */}
            <text x={offset + a/2} y={offset + a/2} fontSize="14" fontWeight="bold" fontStyle="italic" fill="#64748b" textAnchor="middle" dominantBaseline="middle">a²</text>
            <text x={offset + a + b/2} y={offset + a/2} fontSize="12" fontWeight="bold" fontStyle="italic" fill="#1e40af" textAnchor="middle" dominantBaseline="middle">ab</text>
            <text x={offset + a/2} y={offset + a + b/2} fontSize="12" fontWeight="bold" fontStyle="italic" fill="#1e40af" textAnchor="middle" dominantBaseline="middle">ab</text>
            <text x={offset + a + b/2} y={offset + a + b/2} fontSize="12" fontWeight="bold" fontStyle="italic" fill="#64748b" textAnchor="middle" dominantBaseline="middle">b²</text>
          </>
        )}

        {/* Bracket/dimension lines */}
        <line x1={offset} y1={offset - 10} x2={offset + a} y2={offset - 10} stroke="#94a3b8" strokeWidth="1" />
        <line x1={offset + a} y1={offset - 10} x2={offset + outer} y2={offset - 10} stroke="#94a3b8" strokeWidth="1" />
        <line x1={offset - 10} y1={offset} x2={offset - 10} y2={offset + a} stroke="#94a3b8" strokeWidth="1" />
        <line x1={offset - 10} y1={offset + a} x2={offset - 10} y2={offset + outer} stroke="#94a3b8" strokeWidth="1" />
      </svg>
    );
  };

  const renderSimpleSquare = () => {
    // Simple square of side (a+b)
    const outer = size - 60;
    const offset = 30;

    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <rect
          x={offset}
          y={offset}
          width={outer}
          height={outer}
          fill={getShapeColor('full', '#93c5fd')}
          stroke="#1e293b"
          strokeWidth="2"
          style={{ cursor: interactive ? 'pointer' : 'default' }}
          onClick={() => handleShapeClick('full')}
          onMouseEnter={() => setHoveredShape('full')}
          onMouseLeave={() => setHoveredShape(null)}
        />

        {showLabels && (
          <>
            <text x={offset + outer/2} y={offset - 10} fontSize="14" fill="#1e293b" textAnchor="middle">a + b</text>
            <text x={offset + outer + 15} y={offset + outer/2} fontSize="14" fill="#1e293b">a + b</text>
          </>
        )}
      </svg>
    );
  };

  switch (layout) {
    case 'overlapping-squares':
      return renderOverlappingSquares();
    case 'rotated-square':
      return renderRotatedSquare();
    case 'four-rectangles':
      return renderFourRectangles();
    case 'simple-square':
      return renderSimpleSquare();
    default:
      return renderFourRectangles();
  }
};

export default AlgebraicProof;
